import Axios from 'axios'
// For debugging capabilities needs to load from dev distribution.
// import { setupCache, buildWebStorage } from 'axios-cache-interceptor/dev'
import { setupCache, buildWebStorage, buildKeyGenerator } from 'axios-cache-interceptor'
import { isArray, isEmpty, isNumber, isString, isBoolean, trim, toString } from 'lodash'
import { useDispatch, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'

const SLASHES_REGEX = /^\/|\/$/g

export const customCacheKeyGenerator = buildKeyGenerator((requestData) => {
  const { url = '', method = 'get', params, data } = requestData
  const cleanUrl = url.replace(SLASHES_REGEX, '')
  const cleanMethod = method.toLowerCase()

  return {
    url: cleanUrl,
    params: params,
    method: cleanMethod,
    data,
  }
})

const axios = setupCache(Axios, {
  debug: console.log,
  // Cache during 2 minutes.
  ttl: 2 * 60 * 1000,
  // Ignore Drupal API proxy cache headers, preferring to use our custom TTL.
  interpretHeader: false,
  // Store requests cache at session storage.
  storage: buildWebStorage(sessionStorage, 'ubc-cache:'),
  generateKey: customCacheKeyGenerator,
})

/**
 * @typedef {import('axios').AxiosRequestConfig} AxiosRequestConfig
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */

/**
 * @typedef {Object} ApiRequestProps
 * @property {string} baseUrl
 * @property {string=} authToken
 * @property {string=} csrfToken
 */

/**
 * @typedef {Object} DoApiRequestProps
 * @property {string} endpoint
 * @property {any} data
 * @property {Object<string, string>} customHeaders
 * @property {string} method
 */

/**
 * @typedef {(props: DoApiRequestProps) => Promise<any>} DoApiRequest
 * @typedef {(props: Record<string, any>) => string} PrepareQueryParams
 */

/**
 * @typedef {Object} ApiRequestHook
 * @property {DoApiRequest} doApiRequest
 * @property {PrepareQueryParams} prepareQueryParams
 */

/**
 * General API request utility.
 * @param {ApiRequestProps} props
 * @return {ApiRequestHook}
 */
const useApiRequest = ({ baseUrl, authToken, csrfToken }) => {
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const { t } = useI18n()
  const dispatch = useDispatch()

  /**
   * @typedef {Object} CsrfHeader
   * @property {string=} X-CSRF-Token
   */

  /**
   * Compose a CSRF header for API requests.
   * @return {CsrfHeader}
   */
  const prepareCsrfHeader = () => {
    if (isStorybook) {
      return {}
    }

    return {
      'X-CSRF-Token': csrfToken,
    }
  }

  /**
   * Process a Drupal API endpoint request.
   *
   * @type {DoApiRequest}
   * @param {DoApiRequestProps} props
   * @return {Promise<any>}
   */
  const doApiRequest = async ({ endpoint, data = null, customHeaders, method = '' }) => {
    const url = `${baseUrl}${endpoint}`
    const jsonData = data ? JSON.stringify(data) : ''
    const csrfHeader = prepareCsrfHeader()
    const authHeader = {
      Authorization: `Bearer ${authToken}`,
    }
    const errorCodes = [
      400, 401, 403, 404, 405, 406, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504,
    ]

    /**
     * @type {AxiosRequestConfig}
     */
    const options = {
      url,
      method: method || 'POST',
      headers: {
        //'Content-Type': 'application/json',
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        ...(authToken && isStorybook ? authHeader : {}),
        ...(isEmpty(csrfHeader) ? {} : csrfHeader),
        ...(customHeaders || {}),
      },
      timeout: 20000,
    }

    if (data) {
      options.data = jsonData
    } else {
      options.method = method || 'GET'
    }

    // console.log(`doRequestURL: ${url} - ${JSON.stringify(options)}`)
    try {
      /**
       * @type {AxiosResponse}
       */
      const response = await axios(options)
      // If is a success response return data, otherwise null to indicate failure.
      if ([200, 201].includes(response.status)) {
        const contentType = response.headers['content-type']
        if (contentType.includes('json')) {
          return response.data
        } else {
          return {
            data: response.data,
          }
        }
      } else if (errorCodes.includes(response.status)) {
        const errorCode = response.status
        return {
          data: response.data,
          errorCode,
        }
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        dispatch(
          addNotification('api_client', 'error', t('Network timeout, please try to refresh the page.'))
        )
      }
      else if (error.code === 'ERR_BAD_REQUEST') {
        dispatch(
          addNotification('api_client', 'error', t('Data cannot be retrieved from API, please try to refresh the page.'))
        )
      }

      console.log('ERROR API request:', error)
      return null
    }
  }

  /**
   * Prepare query string params.
   * @type {PrepareQueryParams}
   * @param {Record<string, any>} params
   * @return {string}
   */
  const prepareQueryParams = (params) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (isArray(value)) {
        value.forEach((currentValue) => {
          const cleanCurrentValue = trim(currentValue)
          searchParams.append(key, `${cleanCurrentValue}`)
        })
      } else if (isString(value) || isNumber(value) || isBoolean(value)) {
        const cleanValue = trim(toString(value))
        searchParams.append(key, `${cleanValue}`)
      } else {
        console.error(`Parameter ${key} value type not supported:`, value)
      }
    })

    return searchParams.toString()
  }

  return {
    doApiRequest,
    prepareQueryParams,
  }
}

export default useApiRequest
